/**
 * Smart Routing Preview Panel
 * ───────────────────────────
 * Compact read-only overlay that shows the Recipient Resolver's decision
 * for every pharma in the current outreach dialog. Non-invasive — no
 * changes to the existing send flow. Just tells the operator, up front:
 *
 *   Biogen      → generic_mailbox → partnering@biogen.com  (SPC policy)
 *   Sanofi      → ta_matched      → vaccineBD@sanofi.com + Jane Doe
 *   AbbVie      → fallback        → 3 BD contacts (no TA-specific routing)
 *   Novartis    → portal_only     → click to open partnering portal
 *
 * Feb 2026. Wired into `AIMappingPage.js` above the exclusion banner.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, Users, AlertCircle, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RULE_META = {
  generic_mailbox: { label: 'Generic mailbox', tone: 'bg-amber-50 border-amber-200 text-amber-800', Icon: ShieldCheck },
  ta_matched: { label: 'TA-matched', tone: 'bg-emerald-50 border-emerald-200 text-emerald-800', Icon: Users },
  fallback: { label: 'Fallback (all BD)', tone: 'bg-slate-50 border-slate-200 text-slate-700', Icon: Users },
  portal_only: { label: 'Portal only', tone: 'bg-sky-50 border-sky-200 text-sky-800', Icon: Globe },
  no_recipients: { label: 'No recipients', tone: 'bg-rose-50 border-rose-200 text-rose-800', Icon: AlertCircle },
};

export default function SmartRoutingPreviewPanel({ assetId, pharmaCompanies }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const [retryTrigger, setRetryTrigger] = useState(0);
  // Stable key so we don't re-fetch on every parent re-render.
  const cacheKey = useMemo(
    () => `${assetId}::${[...(pharmaCompanies || [])].sort().join('|')}::${retryTrigger}`,
    [assetId, pharmaCompanies, retryTrigger],
  );

  useEffect(() => {
    if (!assetId || !(pharmaCompanies?.length)) return;
    let cancelled = false;
    let attempt = 0;
    const tryFetch = async () => {
      setLoading(true); setError('');
      try {
        const r = await axios.post(
          `${API}/api/recipient-resolver/preview-batch`,
          { asset_id: assetId, pharma_companies: pharmaCompanies },
          { withCredentials: true, timeout: 60000 },
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        // Feb 2026 (v5) — friendlier error state. When Cloudflare 502s
        // (edge timeout on a 90+s batch), the response body is a HTML
        // page with a "could not parse" banner — showing that verbatim
        // is scary UX. Detect that + generic network failures and
        // present a compact retry pill instead. Auto-retry once with a
        // 2s backoff before surfacing the failure to the user.
        const raw = e?.response?.data;
        const isCfBlob = typeof raw === 'string' && raw.includes('Cloudflare');
        const friendly = isCfBlob
          ? 'Routing preview timed out. Sending still works — click Retry to try again.'
          : (e?.response?.data?.detail || e?.message || 'Failed to load routing preview');
        if (attempt < 1 && !cancelled) {
          attempt += 1;
          await new Promise(r => setTimeout(r, 2000));
          if (!cancelled) return tryFetch();
        }
        if (!cancelled) setError(friendly);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tryFetch();
    return () => { cancelled = true; };
  }, [cacheKey, assetId, pharmaCompanies]);

  if (!assetId || !(pharmaCompanies?.length)) return null;

  const s = data?.summary || {};
  const totalTargeted = (s.by_rule?.generic_mailbox || 0) + (s.by_rule?.ta_matched || 0);

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid="smart-routing-preview">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          <ShieldCheck className="w-4 h-4 text-violet-600" />
          <span className="font-semibold text-slate-800">Smart Routing Preview</span>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          {!loading && data && (
            <span className="text-slate-500">
              · {data.summary?.total || 0} compan{data.summary?.total === 1 ? 'y' : 'ies'} ·{' '}
              <strong>{totalTargeted}</strong> targeted routing ·{' '}
              {s.by_rule?.fallback || 0} fallback ·{' '}
              {(s.by_rule?.portal_only || 0) + (s.by_rule?.no_recipients || 0)} skipped
            </span>
          )}
        </div>
        {data?.companies?.length ? (
          <button
            className="text-[11px] text-violet-700 hover:underline"
            onClick={() => setExpanded(!expanded)}
            data-testid="routing-preview-toggle"
          >
            {expanded ? 'Hide per-company detail' : 'Show per-company detail'}
          </button>
        ) : null}
      </div>

      {error && (
        <div className="flex items-center justify-between gap-2 text-[11px]">
          <div className="text-rose-700 flex-1 min-w-0 truncate" title={error}>{error}</div>
          <button
            type="button"
            className="shrink-0 px-2 py-0.5 rounded border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 text-[11px]"
            onClick={() => setRetryTrigger(t => t + 1)}
            data-testid="routing-preview-retry-btn"
          >
            Retry
          </button>
        </div>
      )}

      {expanded && data?.companies?.length > 0 && (
        <div className="max-h-72 overflow-auto border border-slate-200 rounded bg-white" data-testid="routing-preview-list">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">Company</th>
                <th className="text-left px-2 py-1">Rule</th>
                <th className="text-left px-2 py-1">Recipients</th>
                <th className="text-left px-2 py-1">Reasoning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.companies.map((c, i) => {
                const meta = RULE_META[c.rule_applied] || RULE_META.fallback;
                const Icon = meta.Icon;
                return (
                  <tr key={i} className="align-top" data-testid={`routing-row-${i}`}>
                    <td className="px-2 py-1.5 font-medium text-slate-800 whitespace-nowrap">{c.pharma_company}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${meta.tone}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-700">
                      {c.rule_applied === 'portal_only' ? (
                        c.portal_url ? (
                          <a href={c.portal_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                            {c.portal_name || 'Open portal'}
                          </a>
                        ) : <span className="text-slate-400">no portal URL set</span>
                      ) : (c.recipients?.length || 0) === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {c.recipients.slice(0, 3).map((r, j) => (
                            <span key={j} className="truncate max-w-md" title={r.source_detail || ''}>
                              {r.name ? `${r.name} · ` : ''}<span className="text-sky-700">{r.email}</span>
                            </span>
                          ))}
                          {c.recipients.length > 3 && (
                            <span className="text-slate-500">+{c.recipients.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500 max-w-md">
                      <span className="line-clamp-2" title={c.reasoning}>{c.reasoning}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
